import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingAccount } from '../../models/accounting.account.model';
import { AccountingEntryDA } from '../../services/admin/accounting.entry.service';
import { BankStatementDA } from '../../services/admin/bank.statement.service';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide, modalShow } from '../../utils/modal.util';
import { formatAccountingAccount, formatMoney } from '../../scripts/format.value.script';
import { getError } from '../../languages/translations/response';
import { CustomSelectClassNames, CustomSelectStyles } from '../../styles/select.style';

import { Settings } from '../../models/settings.model';
import { ExchangeRateDA } from '../../services/admin/exchange.rate.service';

import AppLoading from '../Loading';
import AppPdfViewer from '../element/PdfViewer';
import AppModalAttachDocument from './ModalAttachDocument';
import { MediaDA } from '../../services/admin/media.service';

export interface ManualEntryBankLine {
  line_id: string;
  id_bank_statement: string;
  amount: number;
  date: string;
  description: string;
  currency_code?: string;
}

export interface AppModalManualEntryProps {
  id_company: string;
  bankLine: ManualEntryBankLine;
  treasuryAccountId: string;
  treasuryAccountLabel: string;
  treasuryName: string;
  accounts: AccountingAccount[];
  journals: Array<{ id: string; type: string; name: string }>;
  companyForUser: any;
  settingsForCompany: Settings | null;
  onSuccess: () => void;
  hasSuggestions?: boolean;
  onSwitchToMatch?: () => void;
}

const AppModalManualEntry: React.FunctionComponent<AppModalManualEntryProps> = ({
  id_company, bankLine, treasuryAccountId, treasuryAccountLabel, treasuryName, accounts, journals, companyForUser, settingsForCompany, onSuccess, hasSuggestions, onSwitchToMatch
}) => {
  const {lang} = StateLanguage()

  const isForeignCurrency = !!bankLine.currency_code && bankLine.currency_code !== 'CHF'
  const foreignCode = bankLine.currency_code ?? 'CHF'
  const isOutflow = bankLine.amount < 0
  const absAmountOriginal = Math.abs(bankLine.amount)

  const [exchangeRate, setExchangeRate] = useState<string>('')
  const rateFetchedRef = useRef(false)

  const absAmount = isForeignCurrency && exchangeRate && parseFloat(exchangeRate) > 0
    ? Math.round((absAmountOriginal / parseFloat(exchangeRate)) * 100) / 100
    : absAmountOriginal

  const defaultJournalId = (): string => {
    const targetType = isOutflow ? 'purchases' : 'sales'
    const found = journals.find(j => j.type === targetType)
    return found?.id ?? (journals.length > 0 ? journals[0].id : '')
  }

  const [journalId, setJournalId] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [counterparts, setCounterparts] = useState<Array<{ id_account: string; amount: number }>>([])
  const [loadIndicator, setLoadIndicator] = useState('off')

  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [pendingInboxDocs, setPendingInboxDocs] = useState<Array<{ id_document_inbox: string; name: string }>>([])
  const hiddenFileInputRef = useRef<HTMLInputElement>(null)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLabel, setPreviewLabel] = useState<string>('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    const element = document.getElementById('modal-manual-entry')
    if (!element) return

    const handleShown = () => {
      setJournalId(defaultJournalId())
      setDate(bankLine.date ? bankLine.date.substring(0, 10) : '')
      setDescription(bankLine.description ?? '')
      setCounterparts([{ id_account: '', amount: absAmountOriginal }])
      setLoadIndicator('off')
      setPendingFiles([])
      setPendingInboxDocs([])
      closePreview()
      setExchangeRate('')
      rateFetchedRef.current = false
      // Fetch suggested exchange rate for foreign currencies
      if (isForeignCurrency) {
        ExchangeRateDA.getRate(id_company, foreignCode, 'CHF').then(res => {
          if (res.status === 200 && res.data?.rate && !rateFetchedRef.current) {
            rateFetchedRef.current = true
            setExchangeRate(String(res.data.rate))
          }
        }).catch(() => {})
      }
    }

    element.addEventListener('shown.bs.modal', handleShown)
    return () => element.removeEventListener('shown.bs.modal', handleShown)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankLine])

  // Update counterpart amounts when exchange rate changes (foreign currency)
  useEffect(() => {
    if (!isForeignCurrency || !exchangeRate) return
    const rate = parseFloat(exchangeRate)
    if (isNaN(rate) || rate <= 0) return
    const chfAmount = Math.round((absAmountOriginal / rate) * 100) / 100
    setCounterparts(prev => {
      if (prev.length === 1 && prev[0].amount !== chfAmount) {
        return [{ ...prev[0], amount: chfAmount }]
      }
      return prev
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeRate])

  useEffect(() => {
    return () => { revokeObjectUrl() }
  }, [])

  const leafAccounts = (accounts ?? []).filter((a: any) => a.type === 'leave' && a.id !== treasuryAccountId)
  const accountOptions = useMemo(() => leafAccounts.map((a: any) => ({value: a.id, label: formatAccountingAccount(a, companyForUser?.language ?? 'fr')})), [leafAccounts, companyForUser])

  const totalCounterparts = counterparts.reduce((sum, c) => sum + (c.amount || 0), 0)
  const gap = Math.round((absAmount - totalCounterparts) * 100) / 100
  const isBalanced = Math.abs(gap) < 0.01

  const allCounterpartsValid = counterparts.length > 0 && counterparts.every(c => c.id_account.length > 0 && c.amount > 0)
  const canSubmit = isBalanced && allCounterpartsValid && journalId.length > 0 && date.length > 0 && description.length > 0 && treasuryAccountId.length > 0

  const handleCounterpartAccount = (index: number, value: string) => {
    const updated = [...counterparts]
    updated[index] = { ...updated[index], id_account: value }
    setCounterparts(updated)
  }

  const handleCounterpartAmount = (index: number, value: string) => {
    const updated = [...counterparts]
    updated[index] = { ...updated[index], amount: parseFloat(value) || 0 }
    setCounterparts(updated)
  }

  const handleAmountBlur = (index: number, e: React.FocusEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    if (!isNaN(val)) {
      e.target.value = val.toFixed(2)
      const updated = [...counterparts]
      updated[index] = { ...updated[index], amount: parseFloat(val.toFixed(2)) }
      setCounterparts(updated)
    }
  }

  const addCounterpart = () => {
    const remaining = Math.round(gap * 100) / 100
    setCounterparts([...counterparts, { id_account: '', amount: remaining > 0 ? remaining : 0 }])
  }

  const removeCounterpart = (index: number) => {
    if (counterparts.length <= 1) return
    setCounterparts(counterparts.filter((_, i) => i !== index))
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    setPendingFiles(prev => [...prev, ...Array.from(files)])
    e.target.value = ''
  }

  const handleInboxPick = (id_document_inbox: string, doc?: { name: string }) => {
    if (pendingInboxDocs.some(d => d.id_document_inbox === id_document_inbox)) return
    setPendingInboxDocs(prev => [...prev, { id_document_inbox, name: doc?.name ?? id_document_inbox }])
  }

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => {
      const removed = prev[index]
      if (removed && previewLabel === removed.name) closePreview()
      return prev.filter((_, i) => i !== index)
    })
  }

  const removePendingInboxDoc = (index: number) => {
    setPendingInboxDocs(prev => {
      const removed = prev[index]
      if (removed && previewLabel === removed.name) closePreview()
      return prev.filter((_, i) => i !== index)
    })
  }

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null }
  }

  const closePreview = () => {
    revokeObjectUrl()
    setPreviewUrl(null)
    setPreviewLabel('')
    setPreviewLoading(false)
  }

  const previewLocalFile = (file: File) => {
    revokeObjectUrl()
    const url = URL.createObjectURL(file)
    objectUrlRef.current = url
    setPreviewLabel(file.name)
    setPreviewUrl(url)
    setPreviewLoading(false)
  }

  const previewInboxDoc = async (doc: { id_document_inbox: string; name: string }) => {
    revokeObjectUrl()
    setPreviewLoading(true)
    setPreviewLabel(doc.name)
    setPreviewUrl(null)
    try {
      const url = await MediaDA.getDocumentSignedUrl(id_company, doc.id_document_inbox)
      if (url) {
        setPreviewUrl(url)
      }
    } catch (_e) {
      setPreviewUrl(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const getCoherenceWarning = (): string | null => {
    const allLeaf = accounts ?? []
    for (const cp of counterparts) {
      if (!cp.id_account) continue
      const acc = allLeaf.find((a: any) => a.id === cp.id_account)
      const code = acc?.code ?? ''
      if (!code) continue
      const prefix = code.charAt(0)
      if (isOutflow && (prefix === '4' || prefix === '5' || prefix === '6')) {
        return lang.labels.directChargeWarning
      }
      if (!isOutflow && prefix === '3') {
        return lang.labels.directRevenueWarning
      }
    }
    return null
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    const warning = getCoherenceWarning()
    if (warning) {
      const result = await GlobalSwal.fire({
        title: lang.labels.warning ?? 'Warning',
        text: warning,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.confirm ?? 'Confirm',
        cancelButtonText: lang.labels.cancel,
      })
      if (!result.isConfirmed) return
    }

    setLoadIndicator('on')

    try {
      const treasuryLine: any = { id_account: treasuryAccountId, amount: absAmount, description, type: 'treasury-account', reference: { id_treasury_account: treasuryAccountId } }
      if (isForeignCurrency && exchangeRate) {
        treasuryLine.amount_original = absAmountOriginal
        treasuryLine.exchange_rate = parseFloat(exchangeRate)
      }
      const cpLines = counterparts.map(c => ({ id_account: c.id_account, amount: c.amount, description }))

      let debits: typeof cpLines
      let credits: typeof cpLines
      if (isOutflow) {
        credits = [treasuryLine]
        debits = cpLines
      } else {
        debits = [treasuryLine]
        credits = cpLines
      }

      const res = await AccountingEntryDA.createEntry(id_company, {
        id_journal: journalId, description, date, type: 'multiple', debits, credits,
        company_tax: {
          subject_tax: settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false,
          tax_method: settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? '',
          tax_declaration: settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? '',
        }
      })
      if (res.status !== 200 && res.status !== 201) {
        const errCode = res.data?.code ?? res.data?.error?.code ?? ''
        GlobalSwal.fire({
          title: errCode ? getError(errCode, lang.code, res.data?.message) : (lang.labels.error ?? 'Error'),
          text: res.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        setLoadIndicator('off')
        return
      }

      const idEntry = res.data?.id ?? res.data?._id
      if (!idEntry) {
        GlobalSwal.fire({ title: lang.labels.error ?? 'Error', text: 'Missing entry ID', icon: 'error', confirmButtonText: lang.labels.okGotIt })
        setLoadIndicator('off')
        return
      }

      const linkRes = await BankStatementDA.matchLine(id_company, bankLine.id_bank_statement, bankLine.line_id, idEntry)
      if (linkRes.status !== 200 && linkRes.status !== 201) {
        GlobalSwal.fire({
          title: lang.labels.error ?? 'Error',
          text: linkRes.data?.message ?? 'Link failed',
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
        setLoadIndicator('off')
        return
      }

      // Attach vouchers (best-effort: entry is already created & matched)
      const failures: string[] = []
      for (const file of pendingFiles) {
        try {
          const r = await AccountingEntryDA.uploadVoucher(id_company, idEntry, file)
          if (r.status !== 200 && r.status !== 201) failures.push(file.name)
        } catch { failures.push(file.name) }
      }
      for (const doc of pendingInboxDocs) {
        try {
          const r = await AccountingEntryDA.attachVoucher(id_company, idEntry, { id_document_inbox: doc.id_document_inbox })
          if (r.status !== 200 && r.status !== 201) failures.push(doc.name)
        } catch { failures.push(doc.name) }
      }

      modalHide('modal-manual-entry')

      if (failures.length > 0) {
        GlobalSwal.fire({
          title: lang.labels.warning ?? 'Warning',
          text: `${lang.labels.entryCreatedVouchersFailed ?? 'Entry created and reconciled, but some documents could not be attached'}: ${failures.join(', ')}`,
          icon: 'warning',
          confirmButtonText: lang.labels.okGotIt
        }).then(() => onSuccess())
      } else {
        onSuccess()
      }
    } catch (err: any) {
      GlobalSwal.fire({
        title: lang.labels.error ?? 'Error',
        text: err?.message ?? 'Unexpected error',
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      })
    } finally {
      setLoadIndicator('off')
    }
  }

  return (
    <div id="modal-manual-entry" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.manualEntry}</h2>
          </div>
          <div className="modal-body" style={{overflow: 'visible'}}>
            { hasSuggestions &&
              <div className="alert alert-warning d-flex align-items-center mb-5">
                <i className="bi bi-exclamation-triangle-fill me-3 fs-3 text-warning"></i>
                <div className="flex-grow-1">
                  {lang.labels.manualEntryMatchesWarning}
                </div>
                { onSwitchToMatch &&
                  <button
                    type="button"
                    className="btn btn-sm btn-warning ms-3 text-nowrap"
                    onClick={() => {
                      modalHide('modal-manual-entry')
                      setTimeout(() => onSwitchToMatch(), 300)
                    }}
                  >
                    {lang.labels.viewMatches}
                  </button>
                }
              </div>
            }
            <div className="d-flex flex-column flex-lg-row">
            <div className={(previewUrl || previewLoading) ? 'flex-lg-row-fluid me-lg-4' : 'w-100'} style={(previewUrl || previewLoading) ? {maxWidth: '50%'} : undefined}>
            <div className="d-flex flex-wrap gap-4 mb-5 p-3 bg-light rounded">
              <div>
                <span className="fw-bold fs-7 text-muted">{lang.labels.date}:</span>
                <span className="ms-2 fw-bolder">{bankLine.date ? bankLine.date.substring(0, 10) : '-'}</span>
              </div>
              <div>
                <span className="fw-bold fs-7 text-muted">{lang.labels.description}:</span>
                <span className="ms-2 fw-bolder">{bankLine.description || '-'}</span>
              </div>
              <div>
                <span className="fw-bold fs-7 text-muted">{lang.labels.amount}:</span>
                <span className={`ms-2 fw-bolder ${isOutflow ? 'text-danger' : 'text-success'}`}>
                  {foreignCode} {formatMoney(bankLine.amount)}
                </span>
                { isForeignCurrency && absAmount > 0 &&
                  <span className="ms-1 text-muted fs-8">(≈ CHF {formatMoney(isOutflow ? -absAmount : absAmount)})</span>
                }
              </div>
            </div>

            { isForeignCurrency &&
              <div className="d-flex align-items-center gap-3 mb-5 p-3 bg-light-primary rounded">
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted text-nowrap fw-bold">1 CHF =</small>
                  <input
                    className="form-control form-control-sm text-end"
                    style={{ width: 100 }}
                    type="number"
                    step="0.000001"
                    min="0"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder={lang.labels.exchangeRate}
                  />
                  <small className="text-muted text-nowrap fw-bold">{foreignCode}</small>
                </div>
                { exchangeRate && parseFloat(exchangeRate) > 0 &&
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                    1 {foreignCode} ≈ {(1 / parseFloat(exchangeRate)).toFixed(6)} CHF
                  </small>
                }
                <div className="ms-auto">
                  <small className="text-muted">{lang.labels.amountInCHF ?? 'Amount in CHF'}:</small>
                  <span className="ms-1 fw-bolder">CHF {formatMoney(absAmount)}</span>
                </div>
              </div>
            }

            <div className="row mb-5">
              <div className="col-md-3 mb-3">
                <label className="form-label required fs-7">{lang.labels.bookingJournal ?? 'Journal'}</label>
                <select className="form-select form-select-sm" value={journalId} onChange={(e) => setJournalId(e.target.value)}>
                  <option value="" className="d-none">{lang.labels.select}</option>
                  {journals.map((j, i) => (
                    <option key={i} value={j.id}>{j.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label required fs-7">{lang.labels.date}</label>
                <input className="form-control form-control-sm" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label required fs-7">{lang.labels.description}</label>
                <input className="form-control form-control-sm" type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>

            <div className="dataTables_wrapper">
              <div className="table-responsive" style={{overflow: 'visible'}}>
                <table className="table align-middle table-row-dashed gy-1 mb-2">
                  <thead className="border-bottom">
                    <tr className="text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-200px text-nowrap text-start">{lang.labels.description}</th>
                      <th className="w-350px text-nowrap text-end">{lang.labels.debit}</th>
                      <th className="w-350px text-nowrap text-end">{lang.labels.credit}</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted fw-bold">
                    {counterparts.map((cp, index) => (
                      <tr key={index}>
                        <td className="text-secondary text-start">
                          <div className="d-flex align-items-center gap-2">
                            <span>{description || '—'}</span>
                            {counterparts.length > 1 && (
                              <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removeCounterpart(index)}>
                                <i className="bi bi-trash fs-5"></i>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-nowrap text-end">
                          {isOutflow && (
                            <div className="d-flex flex-column">
                              <Select menuPlacement="auto" menuPosition="fixed" classNames={CustomSelectClassNames} styles={CustomSelectStyles} components={{DropdownIndicator: null, IndicatorSeparator: null}} placeholder={lang.labels.account} options={accountOptions} value={cp.id_account ? accountOptions.find((o: any) => o.value === cp.id_account) ?? null : null} onChange={(option: any) => handleCounterpartAccount(index, option?.value ?? '')} />
                              <input
                                className="form-control form-control-sm text-end mt-1"
                                type="number"
                                step="0.01"
                                min="0"
                                value={cp.amount || ''}
                                onChange={(e) => handleCounterpartAmount(index, e.target.value)}
                                onBlur={(e) => handleAmountBlur(index, e)}
                              />
                            </div>
                          )}
                        </td>
                        <td className="text-nowrap text-end">
                          {!isOutflow && (
                            <div className="d-flex flex-column">
                              <Select menuPlacement="auto" menuPosition="fixed" classNames={CustomSelectClassNames} styles={CustomSelectStyles} components={{DropdownIndicator: null, IndicatorSeparator: null}} placeholder={lang.labels.account} options={accountOptions} value={cp.id_account ? accountOptions.find((o: any) => o.value === cp.id_account) ?? null : null} onChange={(option: any) => handleCounterpartAccount(index, option?.value ?? '')} />
                              <input
                                className="form-control form-control-sm text-end mt-1"
                                type="number"
                                step="0.01"
                                min="0"
                                value={cp.amount || ''}
                                onChange={(e) => handleCounterpartAmount(index, e.target.value)}
                                onBlur={(e) => handleAmountBlur(index, e)}
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-top text-secondary fw-bolder fs-7">
                    <tr>
                      <td>
                        <div className="d-flex flex-column text-start text-nowrap">
                          <span className="fs-8">{lang.labels.treasuryAccount}</span>
                          <span className="fw-bold">{treasuryName}</span>
                        </div>
                      </td>
                      <td>
                        {!isOutflow && (
                          <div className="d-flex flex-column text-end text-nowrap">
                            <span className="fw-bold">{treasuryAccountLabel}</span>
                            { isForeignCurrency && <span className="fs-8">{foreignCode} {formatMoney(absAmountOriginal)}</span> }
                            <span className="fs-8">CHF {formatMoney(absAmount)}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {isOutflow && (
                          <div className="d-flex flex-column text-end text-nowrap">
                            <span className="fw-bold">{treasuryAccountLabel}</span>
                            { isForeignCurrency && <span className="fs-8">{foreignCode} {formatMoney(absAmountOriginal)}</span> }
                            <span className="fs-8">CHF {formatMoney(absAmount)}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-start">
                        <button type="button" className="btn btn-sm btn-light-primary" onClick={addCounterpart}>
                          + {lang.labels.addLine}
                        </button>
                      </td>
                      <td className="text-end text-nowrap">CHF {formatMoney(isOutflow ? totalCounterparts : absAmount)}</td>
                      <td className="text-end text-nowrap">CHF {formatMoney(isOutflow ? absAmount : totalCounterparts)}</td>
                    </tr>
                    <tr>
                      <td className="text-start text-uppercase">{lang.labels.balance}</td>
                      <td colSpan={2} className={`text-end fw-bolder ${isBalanced ? 'text-success' : 'text-danger'}`}>
                        {isBalanced
                          ? lang.labels.balanced
                          : `${lang.labels.gap}: CHF ${formatMoney(Math.abs(gap))}`
                        }
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="mt-5">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-bold mb-0">{lang.labels.vouchers}</h5>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-light-primary" type="button" onClick={() => modalShow('modal-attach-document-manual-entry')}>
                    <span className="svg-icon svg-icon-3 me-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                        <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.attachExistingDocument}
                  </button>
                  <button className="btn btn-sm btn-light-primary" type="button" onClick={() => hiddenFileInputRef.current?.click()}>
                    <span className="svg-icon svg-icon-3 me-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path opacity="0.3" d="M19 15C19 17.2 17.2 19 15 19H5C2.8 19 1 17.2 1 15C1 13.2 2.2 11.6 4 11.1C3.4 10.4 3 9.5 3 8.5C3 6.6 4.6 5 6.5 5C7.1 5 7.7 5.1 8.2 5.4C9 3.4 11 2 13.5 2C16.5 2 19 4.5 19 7.5C19 8 18.9 8.5 18.8 9C20.6 9.5 22 11.1 22 13C22 15.2 20.2 17 18 17H17" fill="black"/>
                        <path d="M12 8L7 13H10V18H14V13H17L12 8Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.uploadNewPdf}
                  </button>
                  <input
                    ref={hiddenFileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </div>
              </div>
              { (pendingFiles.length > 0 || pendingInboxDocs.length > 0) &&
                <div className="border rounded p-3">
                  { pendingFiles.map((file, index) => (
                    <div key={`file-${index}`} className="d-flex align-items-center justify-content-between py-2 border-bottom last-child-no-border">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 me-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                          </svg>
                        </span>
                        <div>
                          <div className="fw-bold fs-7">{file.name}</div>
                          <div className="text-muted fs-8">{(file.size / 1024).toFixed(1)} Ko</div>
                        </div>
                      </div>
                      <div className="d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-icon btn-light-primary" title={lang.labels.preview ?? 'Preview'} onClick={() => previewLocalFile(file)}>
                          <i className="bi bi-eye fs-5"></i>
                        </button>
                        <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removePendingFile(index)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="currentColor" />
                            <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="currentColor" />
                            <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  { pendingInboxDocs.map((doc, index) => (
                    <div key={`inbox-${index}`} className="d-flex align-items-center justify-content-between py-2 border-bottom last-child-no-border">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 me-2 text-primary">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="currentColor" />
                            <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="currentColor" />
                          </svg>
                        </span>
                        <div>
                          <div className="fw-bold fs-7">{doc.name}</div>
                          <div className="text-muted fs-8">{lang.labels.existingDocument ?? 'Existing document'}</div>
                        </div>
                      </div>
                      <div className="d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-icon btn-light-primary" title={lang.labels.preview ?? 'Preview'} onClick={() => previewInboxDoc(doc)}>
                          <i className="bi bi-eye fs-5"></i>
                        </button>
                        <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removePendingInboxDoc(index)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="currentColor" />
                            <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="currentColor" />
                            <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="currentColor" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              }
            </div>
            </div>
            { (previewUrl || previewLoading) &&
              <div className="flex-lg-row-auto" style={{width: '50%', minHeight: '70vh'}}>
                <div className="card h-100">
                  <div className="card-header py-2 d-flex justify-content-between align-items-center">
                    <h6 className="m-0 text-truncate" style={{maxWidth: '80%'}}>{previewLabel || (lang.labels.preview ?? 'Preview')}</h6>
                    <button className="btn btn-sm btn-icon" type="button" onClick={closePreview}>
                      <i className="bi bi-x fs-4"></i>
                    </button>
                  </div>
                  <div className="card-body p-0" style={{height: '70vh'}}>
                    { previewLoading
                      ? <div className="d-flex justify-content-center align-items-center h-100"><span className="spinner-border text-primary"></span></div>
                      : previewUrl
                        ? <AppPdfViewer pdf={previewUrl} />
                        : <div className="d-flex justify-content-center align-items-center h-100 text-muted">{lang.labels.noPreviewAvailable ?? 'No preview available'}</div>
                    }
                  </div>
                </div>
              </div>
            }
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="button" onClick={() => modalHide('modal-manual-entry')}>{lang.labels.cancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} disabled={!canSubmit} onClick={handleSubmit}>
              <span className="indicator-label">{lang.labels.createAndReconcile}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
      <AppModalAttachDocument
        id_company={id_company}
        entity_type="accounting-entry"
        onAttached={() => {}}
        onPick={handleInboxPick}
        modalId="modal-attach-document-manual-entry"
      />
    </div>
  )
}

export default AppModalManualEntry;
